import type { Placement as PopperPlacement } from '@popperjs/core';
import classnames from 'classnames';
import { ErrorMessagesContext } from 'contexts/errorMessages/ErrorMessages.context';
import { useTranslation } from 'hooks/useTranslation';
import type { IntlKey } from 'locales';
import { RefObject, useContext } from 'react';
import { Overlay, Popover, PopoverBody } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { mediaQueryEqualOrUpper } from 'utils/mediaQueries';

import { List } from '../List/List';

export type Placement = PopperPlacement;

export interface PopoversProps {
  target: RefObject<HTMLElement | HTMLAnchorElement | null>;
  isOpened: boolean;
  onToggle: () => void;
  placement?: Placement;

  color?: 'danger';
}

const ScrollableDiv = styled.div`
  overflow-y: auto;
  height: 40em;
`;

const RestyledPopover = styled(Popover)`
  .popover {
    width: 90vw;
  }

  ${mediaQueryEqualOrUpper('xl')`
  .popover {
    width: 24vw;
  }
  `}

  .card-header,
  .card-body {
    padding: 0.5em;
  }
  .card-header > .btn {
    text-align: left;
  }

  td:nth-child(1) {
    text-align: left;
  }

  td:nth-child(2) {
    text-align: left;
    font-weight: bold;
  }
`;

export function ErrorsPopover({ target, color, isOpened, placement, onToggle }: PopoversProps) {
  const context = useContext(ErrorMessagesContext);
  const { errorMessages, technicalError, hasSummaryFromErrorMessages } = context;
  const errorDetailsErrorTranslation = useTranslation('errorDetails');
  const { formatMessage } = useIntl();

  return (
    <Overlay target={target} show={isOpened} placement={placement} transition={true}>
      <RestyledPopover
        className={classnames({
          'border-danger': color === 'danger',
        })}
      >
        <PopoverBody className="bg-lvl2 text-primary p-2">
          <div
            className={classnames('mt-2 mb-3', {
              'text-danger': color === 'danger',
              'text-primary': color === undefined,
            })}
          >
            <i
              className="icon icon-sm clickable float-end me-2"
              onClick={onToggle}
              data-e2e="error-details-close-button"
            >
              close
            </i>
            <h6>
              <span className="ms-2 align-middle">
                <span className="me-1">{errorDetailsErrorTranslation}</span>
                <span>{errorMessages && '(' + errorMessages.length + ')'}</span>
              </span>
            </h6>
          </div>
          {technicalError && (
            <div
              data-e2e="errors-panel-technical-error"
              className={classnames('ms-2 me-2 mb-2', {
                'text-danger': color === 'danger',
              })}
            >
              {technicalError}
            </div>
          )}
          {!errorMessages &&
            hasSummaryFromErrorMessages === true &&
            context.formattedSummaryMessages.map((msg, messageIndex) => (
              <ul className="m-0 p-0" key={messageIndex} data-e2e="errors-panel-errors-summary">
                {msg.map((line, lineIndex) => (
                  <li
                    className={classnames('ms-2', {
                      'text-danger': color === 'danger',
                    })}
                    key={lineIndex}
                    style={{ listStyle: 'none' }}
                  >
                    <i className="icon icon-sm me-2">cancel</i>
                    <span className="mt-1">{formatMessage({ id: line as IntlKey, defaultMessage: line })}</span>
                  </li>
                ))}
              </ul>
            ))}
          {errorMessages && (
            <ScrollableDiv>
              <List />
            </ScrollableDiv>
          )}
        </PopoverBody>
      </RestyledPopover>
    </Overlay>
  );
}
